import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Seo } from '@/components/Seo';
import { resetPasswordSchema } from '@/lib/auth-validation';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { loading, isPasswordRecovery } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = resetPasswordSchema.safeParse({ password, confirmPassword });
    if (!parsed.success) {
      toast({ title: 'Check your details', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    // Only reachable with a genuine PASSWORD_RECOVERY session (gated below) --
    // updateUser() requires an active session, which is exactly what that
    // session is. Supabase itself enforces this: without one, this call
    // fails rather than silently changing an arbitrary account's password.
    const { error } = await supabase.auth.updateUser({ password: parsed.data.password });
    setSubmitting(false);
    if (error) {
      console.error('Password update failed:', error);
      toast({
        title: 'Something went wrong',
        description: error.message || "We couldn't update your password. Please try again.",
        variant: 'destructive',
      });
      return;
    }
    setSucceeded(true);
  };

  if (loading) {
    return (
      <Layout>
        <Seo title="Reset password | Swell Breaks" description="Choose a new Swell Breaks password." path="/reset-password" noIndex />
        <div className="container mx-auto px-4 py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Seo title="Reset password | Swell Breaks" description="Choose a new Swell Breaks password." path="/reset-password" noIndex />
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">Reset your password</h1>
          </div>

          <Card className="border-border/50 bg-card border-foreground/20">
            {!isPasswordRecovery ? (
              <CardContent className="pt-8 pb-8 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                  <AlertTriangle className="h-7 w-7 text-destructive" />
                </div>
                <CardTitle className="font-serif text-xl mb-2">This link isn't valid</CardTitle>
                <p className="text-sm text-muted-foreground mb-6">
                  This password reset link is invalid, expired, or has already been used. Request a new one to continue.
                </p>
                <Button asChild className="w-full">
                  <Link to="/forgot-password">Request a new link</Link>
                </Button>
              </CardContent>
            ) : succeeded ? (
              <CardContent className="pt-8 pb-8 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="font-serif text-xl mb-2">Password updated</CardTitle>
                <p className="text-sm text-muted-foreground mb-6">Your password has been changed successfully.</p>
                <Button className="w-full" onClick={() => navigate('/my-swell-breaks')}>
                  Continue to My Swell Breaks
                </Button>
              </CardContent>
            ) : (
              <>
                <CardHeader className="pb-2">
                  <CardTitle className="font-serif text-xl mb-1">Choose a new password</CardTitle>
                  <CardDescription>Make it at least 8 characters.</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="rp-password">New password</Label>
                      <Input
                        id="rp-password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rp-confirm-password">Confirm new password</Label>
                      <Input
                        id="rp-confirm-password"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Update password
                    </Button>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default ResetPassword;
